import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { i18n } from '@avanta/shared/i18n';
import { type Patient } from '@avanta/shared/types/patients';
import { PRIVATE_ROUTES, ROUTES } from 'constants/routes';
import { ReportService } from 'services/reports';
import {
  type MessageType,
  MESSAGE_SENDERS,
  QUESTION_TYPES,
  type OptionType,
  type AnswerType,
} from 'types/sapa';
import { getUserId } from 'utils/storage';
import { questions } from '../utils/questionFlow';
import {
  QUESTION_IDS,
  NAVIGATION_ACTIONS,
  MESSAGE_ID_PREFIXES,
  TEXT_SEPARATORS,
  TIMEOUTS,
  ARRAY_LENGTHS,
} from './constants';
import { type Question, type TrackSapaFormSubmitted } from './types';

type UserAnswers = Record<string, AnswerType>;

export interface DetermineNextFlowParams {
  nextId: string;
  navigate: (path: string, options?: { state?: Record<string, unknown> }) => void;
  setIsFinished: (finished: boolean) => void;
  setCurrentQuestionId: (id: string) => void;
  addMessage: (message: MessageType) => void;
  answers: Record<string, unknown>;
  questionId: string;
  answerWithText: unknown;
  user: Patient | undefined;
  companyName?: string;
  trackSapaFormSubmitted?: TrackSapaFormSubmitted;
}

export const createUserMessage = (content: string): MessageType => ({
  id: `${MESSAGE_ID_PREFIXES.USER}-${Date.now()}`,
  sender: MESSAGE_SENDERS.USER,
  content,
  type: QUESTION_TYPES.INFO,
});

export const createSystemMessage = (question: Question): MessageType => ({
  id: `${MESSAGE_ID_PREFIXES.SYSTEM}-${Date.now()}`,
  sender: MESSAGE_SENDERS.SYSTEM,
  content: question.text,
  options: question.options,
  type: question.type,
  questionId: question.id,
  hasTextInput: question.hasTextInput,
  textInputLabel: question.textInputLabel,
});

export const formatSelectedOptions = (
  options: OptionType[] | undefined,
  answer: unknown
): string => {
  if (!options) return '';

  return options
    .filter((opt: OptionType) =>
      Array.isArray(answer) ? answer.includes(opt.value) : opt.value === answer
    )
    .map((opt: OptionType) => opt.text)
    .join(TEXT_SEPARATORS.DOUBLE_LINE_BREAK);
};

export const formatUserResponse = (selectedOptions: string, textInput?: string): string => {
  let content = selectedOptions || '';
  if (textInput) {
    content += `${TEXT_SEPARATORS.DOUBLE_LINE_BREAK}${textInput}`;
  }
  return content;
};

export const extractAnswerForNextQuestion = (answer: unknown, questionType: string): unknown => {
  if (
    questionType === QUESTION_TYPES.SINGLE &&
    Array.isArray(answer) &&
    answer.length === ARRAY_LENGTHS.SINGLE_ITEM
  ) {
    return answer[0];
  }
  return answer;
};

const buildUserData = (
  user: Patient,
  companyName?: string
): {
  email: string;
  company: string;
  nickname: string;
  gender: number;
  tlfnOrEmail?: string;
} => ({
  email: user.email,
  company: companyName ?? i18n.t('Sapa.company.empty', { ns: LOCIZE_NS.SERVICES }),
  nickname: user.nickname,
  gender: user.gender,
});

const handleChatAgentNavigation = (
  navigate: (path: string, options?: { state?: Record<string, unknown> }) => void,
  setIsFinished: (finished: boolean) => void
): void => {
  const userId = getUserId();
  if (userId) {
    navigate(ROUTES.CHAT, { state: { psychologist: true } });
  } else {
    setIsFinished(true);
  }
};

const handleAppointmentNavigation = (navigate: (path: string) => void): void => {
  navigate(PRIVATE_ROUTES?.APPOINTMENTS_RESERVE);
};

const processNextQuestion = (
  nextId: string,
  setCurrentQuestionId: (id: string) => void,
  addMessage: (message: MessageType) => void
): void => {
  setCurrentQuestionId(nextId);
  const nextQuestion = questions[nextId] as Question;
  setTimeout(() => {
    addMessage(createSystemMessage(nextQuestion));
  }, TIMEOUTS.MESSAGE_DELAY);
};

const extractContactInfoFromAnswer = (
  answerWithText: unknown
): { contactInfo: string | undefined; normalizedAnswer: unknown } => {
  if (
    !answerWithText ||
    typeof answerWithText !== 'object' ||
    !('textInput' in (answerWithText as Record<string, unknown>))
  ) {
    return { contactInfo: undefined, normalizedAnswer: answerWithText };
  }
  const answerObject = answerWithText as { answer?: unknown; textInput?: string };
  const normalizedAnswer =
    'answer' in answerObject && answerObject.answer !== undefined
      ? answerObject.answer
      : answerWithText;
  return { contactInfo: answerObject.textInput, normalizedAnswer };
};

const handleConclusionFlow = (params: DetermineNextFlowParams): void => {
  const { questionId, nextId, answers, answerWithText, user, companyName, trackSapaFormSubmitted } =
    params;
  if (questionId !== QUESTION_IDS.CONCLUSION || nextId !== QUESTION_IDS.END || !user) {
    return;
  }
  const baseUserData = buildUserData(user, companyName);
  const { contactInfo, normalizedAnswer } = extractContactInfoFromAnswer(answerWithText);
  const updatedAnswers = { ...answers, [questionId]: normalizedAnswer };
  const userData = {
    ...baseUserData,
    ...(contactInfo ? { tlfnOrEmail: contactInfo } : {}),
  };
  const reportData = {
    timestamp: new Date().toISOString(),
    userData,
    answers: updatedAnswers as UserAnswers,
  };
  trackSapaFormSubmitted?.(reportData);
  void ReportService.sendSapaReport(reportData.answers, reportData.userData);
};

export const determineNextFlow = (params: DetermineNextFlowParams): void => {
  const { nextId, navigate, setIsFinished, setCurrentQuestionId, addMessage } = params;

  if (nextId === NAVIGATION_ACTIONS.NAVIGATE_TO_CHAT_AGENT) {
    handleChatAgentNavigation(navigate, setIsFinished);
    return;
  }
  if (nextId === NAVIGATION_ACTIONS.NAVIGATE_TO_APPOINTMENT_RESERVE) {
    handleAppointmentNavigation(navigate);
    return;
  }
  handleConclusionFlow(params);
  if (nextId === QUESTION_IDS.END) {
    setIsFinished(true);
    return;
  }
  if (nextId) {
    processNextQuestion(nextId, setCurrentQuestionId, addMessage);
  }
};
